// Settings → Additional attributes: the transaction's STANDARD additional
// attributes (`transaction.uda`, the design layer — plan §8).
//
// The shared setup screen (@twasta/page-runtime/uda) in DESIGN mode: the same
// editors an organization's func-admin uses from Customize, editing this
// model's `uda` block instead of a tenant draft. Every change goes through
// `onModelChange`; the designer's own Save writes the model file, so there is
// no publish or history here. What each organization may change on a standard
// item (`tenant_may`) and whether it may add its own
// (`allow_tenant_additions`) are set on the items themselves.
//
// The screen reads master values and the catalog through the platform's
// /api/uda transport, and renders its previews with the designer's form
// runtime.

import { useCallback, useMemo } from 'react'
import { TxnRuntimeProvider } from '@twasta/page-runtime/txn'
import { UdaSetupScreen, type UdaLayer } from '@twasta/page-runtime/uda'
import { makeDesignerTxnRuntime } from '@/livepreview/runtime/designerTxnRuntime'
import { platformUdaTransport } from '@/livepreview/runtime/udaTransport'
import type { TransactionModel } from './types'

interface Props {
  model: TransactionModel
  onModelChange: (m: TransactionModel) => void
  project: string
  /** The designer's own business-logic editor (PropertyPanel's onOpenCode):
   *  a standard attribute's function is the transaction's business logic. */
  onOpenCode?: (method?: string) => void
}

function isEmptyLayer(layer: UdaLayer | undefined): boolean {
  return !layer || !Object.keys(layer.forms || {}).length
}

export default function AdditionalAttributesSettings({ model, onModelChange, project, onOpenCode }: Props) {
  const runtime = useMemo(() => makeDesignerTxnRuntime(project), [project])
  const transport = useMemo(() => platformUdaTransport(project), [project])
  const onChange = useCallback((layer: UdaLayer) => {
    const next: TransactionModel = { ...model }
    if (isEmptyLayer(layer)) delete next.uda
    else next.uda = layer
    onModelChange(next)
  }, [model, onModelChange])

  if (!model.obj_name) {
    return (
      <div style={{ padding: 12, fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
        Name and save the transaction first — additional attributes are set up per object.
      </div>
    )
  }
  return (
    <div style={{ padding: '4px 4px 8px' }}>
      <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: '4px 6px 10px', lineHeight: 1.5 }}>
        Extra details a record is asked for, depending on its values (a customer&apos;s type, an
        item&apos;s kind). The attributes set up here are standard for every organization using the
        application; mark on each item what an organization may change, and whether it may add
        attributes of its own. Saved with the transaction.
      </p>
      <TxnRuntimeProvider value={runtime}>
        <UdaSetupScreen
          mode="design"
          layer={model.uda}
          onChange={onChange}
          model={model}
          transport={transport}
          project={project}
          objName={model.obj_name}
          onClose={() => { /* design mode has no screen to return to */ }}
          onOpenDesignerCode={onOpenCode ? (method) => onOpenCode(method) : undefined}
        />
      </TxnRuntimeProvider>
    </div>
  )
}
